import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import { styled } from "@mui/material/styles";
import Grid from "@mui/material/Unstable_Grid2";
import clsx from "clsx";
import { useState } from "react";
import { useDispatch } from "react-redux";
import SRC_TYPES from "../../dictionaries/srcTypes.json";
import {
  calculateSaleFields,
  edit as editAirSale,
  expandSale,
} from "../../redux/sales";
import { formatDate, formatDateTime, getUserFullName } from "../../utils";
import RowDetailField from "../RowDetailField";
import SaleTypeIndicator from "../SaleTypeIndicator";
import SaleRowContextMenu from "./SaleRowContextMenu";

const isEditable = (sale) => ["OR", "OS"].includes(sale.type);

const OtherSaleRow = (props) => {
  const dispatch = useDispatch();

  const [contextMenu, setContextMenu] = useState(null);
  const [contextMenuTargetValue, setContextMenuTargetValue] = useState(null);

  const sale = props.sale;

  const canEdit = props.canEdit && isEditable(sale);

  const isRefund = sale.type === "OR";

  const { commission, penalty, price, serviceFee, tax, total } =
    calculateSaleFields(sale);

  const onEditSale = () => {
    dispatch(editAirSale({ id: sale.id }));
  };

  const onExpand = (event, expanded) => {
    dispatch(expandSale({ id: sale.id, expanded }));
  };

  const closeContextMenu = () => {
    setContextMenu(null);
  };

  const showContextMenu = (targetValue) => (event) => {
    setContextMenuTargetValue(targetValue || event.target?.innerText);
    setContextMenu(
      contextMenu === null
        ? { mouseX: event.clientX, mouseY: event.clientY }
        : null,
    );
  };

  const onContextMenu = (targetValue) => (event) => {
    event.preventDefault();
    event.stopPropagation();
    showContextMenu(targetValue)(event);
  };

  const [touchTimeoutId, setTouchTimeoutId] = useState(null);
  const TOUCH_DURATION = 1000;

  const onTouchStart = (targetValue) => (event) => {
    setTouchTimeoutId(
      setTimeout(() => {
        showContextMenu(targetValue)(event.touches[0]);
      }, TOUCH_DURATION),
    );
  };

  const onTouchEnd = () => {
    clearTimeout(touchTimeoutId);
  };

  const onTouchMove = () => {
    clearTimeout(touchTimeoutId);
  };

  const isEdited = !!sale?._prev;

  return (
    <Accordion
      className={clsx(props.className, {
        IsEdited: isEdited,
        IsRefund: isRefund,
      })}
      disableGutters
      elevation={0}
      expanded={sale.expanded || false}
      onChange={onExpand}
      square
      TransitionProps={{ unmountOnExit: true }}
      onContextMenu={onContextMenu()}
      onTouchStart={onTouchStart()}
      onTouchEnd={onTouchEnd}
      onTouchMove={onTouchMove}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <SaleRowContextMenu
          contextMenu={contextMenu}
          onClose={closeContextMenu}
          targetValue={contextMenuTargetValue}
          canEdit={canEdit}
          onEdit={onEditSale}
          pnr={sale.pnr}
        />
        <Grid container xs={12} spacing={1}>
          <Grid xs={5} sm={3} md={2} lg={1.5}>
            {formatDate(sale.saleDate)}
          </Grid>
          <Grid xs={4.5} sm={4} md={2.75} lg={2.25}>
            <SaleTypeIndicator className="saleType" value={sale.type} />
            <span>{sale.pax}</span>
          </Grid>
          <Grid
            display={{ xs: "none", md: "block" }}
            md={1}
            lg={1.75}
            title={sale.pnr}
          >
            <span>{sale.pnr}</span>
          </Grid>
          <Grid display={{ xs: "none", sm: "block" }} sm={3} md={2.5} lg={1.5}>
            {sale.loc}
          </Grid>
          <Grid
            className="saleTotal"
            display={{ xs: "none", md: "block" }}
            md={1.25}
          >
            {price.toFormat(2)}
          </Grid>
          <Grid
            className="saleTotal"
            display={{ xs: "none", lg: "block" }}
            lg={1.25}
          >
            {serviceFee.toFormat(2)}
          </Grid>
          <Grid
            className="saleTotal"
            display={{ xs: "none", md: "block" }}
            md={1.25}
          >
            {tax.toFormat(2)}
          </Grid>
          <Grid xs={2.5} className="saleTotal" sm={2} md={1.25}>
            {total.toFormat(2)}
          </Grid>
        </Grid>
      </AccordionSummary>
      <AccordionDetails sx={{ paddingTop: "0px" }}>
        <Grid container className="detailsContainer" xs={12} spacing={1}>
          <RowDetailField label="Service Date" value={sale.serviceDate} />
          <RowDetailField
            display={{ sm: "none" }}
            label="Service Location"
            value={sale.loc}
          />
          {sale.pnr && (
            <RowDetailField
              label="Confirmation #"
              value={sale.pnr}
              href={`pnr://${sale.pnr}`}
            />
          )}
          {sale.sa && (
            <RowDetailField label="Sales Agent" value={sale.sa.name} />
          )}
          {sale.customer && (
            <RowDetailField
              label="Customer"
              value={sale?.customer?.name || ""}
            />
          )}
          {sale.sup && (
            <RowDetailField label="Supplier" value={sale?.sup?.name || ""} />
          )}
          {sale.src && (
            <RowDetailField
              label="Source"
              value={SRC_TYPES[sale.src]?.name || sale.src}
            />
          )}
          <RowDetailField label="Payment Method" value={sale.paymentMethod} />
          <RowDetailField
            display={{ md: "none" }}
            label="Price"
            value={price.toFormat(2)}
          />
          {sale.commission && (
            <RowDetailField label="Commission" value={commission.toFormat(2)} />
          )}
          {sale.tax && (
            <RowDetailField
              display={{ md: "none" }}
              label="Tax"
              value={tax.toFormat(2)}
            />
          )}
          {isRefund && (
            <RowDetailField label="Penalty" value={penalty.toFormat(2)} />
          )}
          {sale.serviceFee && (
            <RowDetailField
              display={{ lg: "none" }}
              label="Service Fee"
              value={serviceFee.toFormat(2)}
            />
          )}
          {sale.savedBy && (
            <RowDetailField
              label="Saved By"
              value={getUserFullName(sale.savedBy)}
            />
          )}
          <RowDetailField
            label="Saved At"
            value={formatDateTime(sale.savedAt)}
          />
          {sale.descr && (
            <RowDetailField
              label="Description"
              title={sale.descr}
              pre
              value={sale.descr}
            />
          )}
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
};

export default styled(OtherSaleRow)(
  ({ theme }) => `
  &:hover {
    background-image:
      repeating-linear-gradient(90deg, ${theme.palette.secondary.main}, ${
        theme.palette.secondary.main
      } 1px, transparent 1px, transparent 2px, ${theme.palette.secondary.main} 2px),
      repeating-linear-gradient(90deg, ${theme.palette.secondary.main}, ${
        theme.palette.secondary.main
      } 1px, transparent 1px, transparent 2px, ${theme.palette.secondary.main} 2px);
    background-size: 100% 1px;
    background-position: 0 0, 0 100%;
    background-repeat: no-repeat;
  }

  &.IsEdited .MuiAccordionSummary-root {
    text-decoration: underline wavy ${theme.palette.secondary.main};
  }

  &.IsRefund .MuiAccordionSummary-root {
    color: ${theme.palette.tertiary.main};
    font-family: milliard-light;
  }

  &.IsVoid .MuiAccordionSummary-root {
    color: ${theme.palette.grey["600"]};
    font-family: milliard-light;
  }

  .MuiAccordionSummary-root {
    padding-right: 6px;
  }

  &.Mui-expanded {
    background-image:
      repeating-linear-gradient(90deg, ${theme.palette.background.paper}, ${
        theme.palette.background.paper
      } 1px, transparent 1px, transparent 2px, ${
        theme.palette.background.paper
      } 2px),
      linear-gradient(90deg, ${theme.palette.secondary.main} 30%, ${
        theme.palette.primary.main
      } 95%),
      repeating-linear-gradient(90deg, ${theme.palette.background.paper}, ${
        theme.palette.background.paper
      } 1px, transparent 1px, transparent 2px, ${
        theme.palette.background.paper
      } 2px),
      linear-gradient(90deg, ${theme.palette.secondary.main} 30%, ${
        theme.palette.primary.main
      } 95%);
    background-size: 100% 1px;
    background-position: 0 0, 0 0, 0 100%, 0 100%;
    background-repeat: no-repeat;
  }

  .MuiIconButton-root {
    padding: 0;
  }

  .MuiIconButton-root .MuiSvgIcon-root {
    font-size: 22px;
  }

  .detailsContainer {
    overflow: hidden;
  }

  .saleTotal {
    text-align: right;
  }

  .saleType {
    margin-left: -40px;
    position: absolute;
  }
`,
);
